import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
	getPromoClientsAdmin,
	deletePromoClient,
	movePromoClient,
} from "../../../../api/promo-client";
import { getPromoClientSectorsAdmin } from "../../../../api/promo-client-sector";
import {
	Button,
	Tooltip,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { buttonsStyle } from "../../../components/tables/table";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { SERVER_URL } from "../../../../utils";

function resolveLogoUrl(raw) {
	const p = String(raw || "").trim();
	if (!p) return "";
	if (/^https?:\/\//i.test(p) || p.startsWith("data:")) return p;
	if (p.startsWith("/")) return `${String(SERVER_URL || "").replace(/\/$/, "")}${p}`;
	return `${String(SERVER_URL || "").replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
}

function groupClientsBySector(clients, sectors) {
	const sectorList = [...(sectors || [])].sort(
		(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name))
	);
	const bySector = new Map(sectorList.map((s) => [String(s._id), { sector: s, clients: [] }]));

	for (const client of clients || []) {
		const sid = String(client.sectorId?._id || client.sectorId || "");
		if (!bySector.has(sid)) {
			bySector.set(sid, {
				sector: { _id: sid, name: client.sectorId?.name || "Sin sector", sortOrder: 999 },
				clients: [],
			});
		}
		bySector.get(sid).clients.push(client);
	}

	return [...bySector.values()]
		.map((g) => ({
			...g,
			clients: [...g.clients].sort(
				(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name))
			),
		}))
		.filter((g) => g.clients.length > 0);
}

export default function PromoClientsPage() {
	const [clients, setClients] = useState([]);
	const [sectors, setSectors] = useState([]);
	const [clientId, setClientId] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [refresh, setRefresh] = useState(false);
	const [movingId, setMovingId] = useState(null);
	const history = useHistory();

	const grouped = useMemo(() => groupClientsBySector(clients, sectors), [clients, sectors]);

	const loadData = () => {
		let mounted = true;
		Promise.all([getPromoClientsAdmin(), getPromoClientSectorsAdmin()])
			.then(([clientsRes, sectorsRes]) => {
				if (!mounted) return;
				if (clientsRes.status === 200) setClients(clientsRes.data || []);
				if (sectorsRes.status === 200) setSectors(sectorsRes.data || []);
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudieron cargar los clientes." });
			})
			.finally(() => {
				if (mounted) setRefresh(false);
			});
		return () => {
			mounted = false;
		};
	};

	useEffect(() => {
		return loadData();
	}, [refresh]);

	const handleMove = (id, direction) => {
		setMovingId(id);
		movePromoClient(id, direction)
			.then((res) => {
				if (res.status === 200) setRefresh(true);
			})
			.catch((error) => {
				alertError({ error, customMessage: "No se pudo cambiar el orden." });
			})
			.finally(() => setMovingId(null));
	};

	return (
		<>
			<Card>
				<CardHeader title="Clientes web (evenely.com)">
					<CardHeaderToolbar>
						<Button
							variant="outlined"
							color="default"
							style={{ marginRight: 8 }}
							onClick={() => history.push("/promo-client-sectors")}
						>
							Gestionar sectores
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={() => history.push("/edit-promo-client")}
						>
							Nuevo cliente
						</Button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					{grouped.length === 0 ? (
						<Typography color="textSecondary">No hay clientes. Crea el primero con «Nuevo cliente».</Typography>
					) : (
						grouped.map((group) => (
							<div key={group.sector._id} style={{ marginBottom: 28 }}>
								<Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
									{group.sector.name}
									{group.sector.active === false ? (
										<Chip label="Sector inactivo" size="small" style={{ marginLeft: 8 }} />
									) : null}
								</Typography>
								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell width={120}>Logo</TableCell>
												<TableCell>Nombre</TableCell>
												<TableCell width={100}>Home</TableCell>
												<TableCell width={100}>Estado</TableCell>
												<TableCell width={200} align="right">
													Acciones
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{group.clients.map((client, index) => {
												const id = client._id;
												const src = resolveLogoUrl(client.logoUrl);
												const isFirst = index === 0;
												const isLast = index === group.clients.length - 1;
												const active = client.active !== false;
												const showOnHome = client.showOnHome !== false;
												return (
													<TableRow key={id}>
														<TableCell>
															{src ? (
																<img
																	src={src}
																	alt=""
																	style={{ maxHeight: 36, maxWidth: 100, objectFit: "contain" }}
																	onError={(e) => {
																		e.currentTarget.style.display = "none";
																	}}
																/>
															) : (
																"—"
															)}
														</TableCell>
														<TableCell>{client.name}</TableCell>
														<TableCell>
															{showOnHome && active ? (
																<Chip label="Sí" size="small" color="primary" />
															) : (
																<Chip label="No" size="small" variant="outlined" />
															)}
														</TableCell>
														<TableCell>
															{active ? (
																<Chip label="Activo" size="small" style={{ background: "#1bc5bd", color: "#fff" }} />
															) : (
																<Chip label="Inactivo" size="small" />
															)}
														</TableCell>
														<TableCell align="right">
															<Tooltip title="Editar">
																<Button
																	size="small"
																	style={buttonsStyle}
																	onClick={() => history.push("/edit-promo-client/" + id)}
																>
																	<EditIcon />
																</Button>
															</Tooltip>
															<Tooltip title="Eliminar">
																<Button
																	size="small"
																	style={buttonsStyle}
																	onClick={() => {
																		setClientId(id);
																		setOpenConfirmDialog(true);
																	}}
																>
																	<DeleteIcon />
																</Button>
															</Tooltip>
															<Tooltip title="Subir">
																<span>
																	<Button
																		size="small"
																		style={buttonsStyle}
																		disabled={isFirst || movingId === id}
																		onClick={() => handleMove(id, "up")}
																	>
																		<ArrowUpwardIcon fontSize="small" />
																	</Button>
																</span>
															</Tooltip>
															<Tooltip title="Bajar">
																<span>
																	<Button
																		size="small"
																		style={buttonsStyle}
																		disabled={isLast || movingId === id}
																		onClick={() => handleMove(id, "down")}
																	>
																		<ArrowDownwardIcon fontSize="small" />
																	</Button>
																</span>
															</Tooltip>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</div>
						))
					)}
					<ConfirmDialog
						title="¿Seguro que quieres eliminar este cliente?"
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePromoClient(clientId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Cliente eliminado correctamente." });
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar el cliente." });
								})
								.finally(() => {
									setOpenConfirmDialog(false);
									setClientId(null);
								});
						}}
					/>
				</CardBody>
			</Card>
		</>
	);
}
